import { useRef, useEffect } from "react"
import { TextField, TextFieldVariants } from "@mui/material"
import { useJsApiLoader } from "@react-google-maps/api"
import { ReactNode } from "react"
import { Control, Controller, FieldValues, Path } from "react-hook-form"

interface ControlledGoogleAddressTypes<T extends FieldValues> {
  formInputStyling?: object
  name: Path<T>
  label?: string
  type?: string
  props?: object
  control: Control<T>
  endAdornment?: ReactNode
  startAdornment?: ReactNode
  variant?: TextFieldVariants
  placeholder?: string
  setValue: (value: string) => void
}

const libraries: ["places"] = ["places"]

const ControlledGoogleAddress = <T extends FieldValues>({
  formInputStyling,
  control,
  name,
  label,
  type,
  placeholder,
  variant,
  endAdornment,
  startAdornment,
  setValue,
}: ControlledGoogleAddressTypes<T>) => {
  const autoCompleteRef = useRef<google.maps.places.Autocomplete | null>(null)
  const inputRef = useRef<HTMLInputElement | null>(null)

  const { isLoaded } = useJsApiLoader({
    id: "google-map-script",
    googleMapsApiKey: import.meta.env.VITE_GOOGLE_MAP_KEY, // Make sure this is set correctly
    libraries,
  })

  const options: google.maps.places.AutocompleteOptions = {
    componentRestrictions: { country: "us" },
    fields: ["address_components", "formatted_address", "geometry", "name"],
  }

  useEffect(() => {
    if (isLoaded && inputRef.current) {
      autoCompleteRef.current = new window.google.maps.places.Autocomplete(
        inputRef.current,
        options
      )

      autoCompleteRef.current.addListener("place_changed", () => {
        const place = autoCompleteRef.current?.getPlace()

        console.log(place)
        if (place && place.name) {
          setValue(`${place.formatted_address}`)
          // Update the controlled input value with the selected place's name
          // control.setValue(name, place.name);
        }
      })
    }
  }, [isLoaded, control, name])

  return (
    <>
      <Controller
        control={control}
        name={name}
        render={({ field, fieldState }) => (
          <TextField
            sx={formInputStyling}
            type={type}
            label={label}
            placeholder={placeholder}
            inputRef={inputRef}
            {...field}
            variant={variant}
            InputProps={{
              startAdornment: startAdornment,
              endAdornment: endAdornment,
            }}
            onChange={(e) => {
              console.log(e, fieldState.error)
              // Update the form value on input change
              field.onChange(e)
            }}
            error={fieldState.invalid}
            helperText={fieldState.error?.message}
          />
        )}
      />
    </>
  )
}

export default ControlledGoogleAddress
